/**
 * ステップ統合（複数の旧カードを 1 画面へまとめる）の解決ロジック。Vue 非依存。
 *
 * カード定義に `sections: [...旧カード ID]` を書くと、その画面が旧カードの
 * 中身をまとめて描画していることを表す。カード側のコンポーネントは
 * `isCardSectionActive()` で表示判定し、旧 URL / 保存済みステップは
 * `resolveCardId()` で統合後のカードへ振り替える。
 *
 * 統合していない書式（`sections` を持たないカード定義）の挙動は変わらない。
 */
import { getFormatById } from '@/utils/format-edit/getFormatById'

export type FormatCard = {
  id: string
  title?: string
  previewJumpId?: string
  /** この画面がまとめて描画する旧カード ID */
  sections?: string[]
}

function formatCards(formatId: string | undefined | null): FormatCard[] {
  if (!formatId) return []
  const cards = getFormatById(formatId)?.cards
  return Array.isArray(cards) ? (cards as FormatCard[]) : []
}

/**
 * 表示中のカードが `section`（旧カード ID）の中身を描画するか。
 * 統合していない書式では `currentCardId === section` と等価。
 */
export function isCardSectionActive(
  formatId: string | undefined | null,
  currentCardId: string | undefined | null,
  section: string
): boolean {
  if (!currentCardId) return false
  if (currentCardId === section) return true
  const card = formatCards(formatId).find((item) => item.id === currentCardId)
  return !!card?.sections?.includes(section)
}

/**
 * 旧カード ID を、それを取り込んだ現行カード ID へ解決する。
 * その書式に存在する ID、または解決先が無い ID はそのまま返す。
 */
export function resolveCardId(
  formatId: string | undefined | null,
  cardId: string | undefined | null
): string | undefined | null {
  if (!cardId) return cardId
  const cards = formatCards(formatId)
  if (!cards.length) return cardId
  if (cards.some((card) => card.id === cardId)) return cardId
  const owner = cards.find((card) => card.sections?.includes(cardId))
  return owner ? owner.id : cardId
}
